<template>
  <div class="mb-2 d-flex flex-column">
    <div class="d-flex align-items-center">
      <h6 class="mb-0">
        {{ title }}
      </h6>
      <feather-icon
        v-if="!canUseSlider"
        :id="`slider-lock-icon-${constraintKey}`"
        icon="LockIcon"
        size="14"
        class="ml-50 text-muted"
      />
      <b-tooltip
        v-if="!canUseSlider"
        :target="`slider-lock-icon-${constraintKey}`"
        triggers="hover"
        placement="top"
      >
        {{ premiumFeatureTooltip }}
      </b-tooltip>
    </div>
    <span
      :id="`slider-wrapper-${constraintKey}`"
      class="d-block"
    >
      <div
        class="px-2 py-2"
        :class="{ 'slider-disabled': !canUseSlider }"
      >
        <vue-slider
          :value="sliderValue"
          :data="allDataPoints"
          :marks="computedMarks"
          :direction="direction"
          :adsorb="true"
          :tooltip="'active'"
          :tooltip-formatter="formatValue"
          :enable-cross="true"
          :lazy="true"
          :disabled="!canUseSlider"
          :dot-style="{
            backgroundColor: '#ffffff',
            border: '2px solid #1a73e8',
            boxShadow: '0 2px 4px rgba(26, 115, 232, 0.4)'
          }"
          :process-style="{
            backgroundColor: '#1a73e8'
          }"
          :tooltip-style="{
            backgroundColor: '#1a73e8',
            borderColor: '#1a73e8',
            color: '#ffffff',
            fontSize: '12px'
          }"
          @drag-start="handleDragStart"
          @dragging="handleDragging"
          @drag-end="handleDragEnd"
          @change="handleChange"
        />
      </div>
    </span>
    <b-tooltip
      v-if="!canUseSlider"
      :target="`slider-wrapper-${constraintKey}`"
      triggers="hover"
      placement="top"
    >
      {{ premiumFeatureTooltip }}
    </b-tooltip>
    <div class="slider-range-display">
      <small class="text-muted">
        <template v-if="sliderValue[0] === sliderValue[1]">
          {{ formatDisplayValue(sliderValue[0]) }}
        </template>
        <template v-else-if="direction === 'rtl'">
          {{ formatDisplayValue(sliderValue[1]) }} - {{ formatDisplayValue(sliderValue[0]) }}
        </template>
        <template v-else>
          {{ formatDisplayValue(sliderValue[0]) }} - {{ formatDisplayValue(sliderValue[1]) }}
        </template>
      </small>
    </div>
  </div>
</template>

<script>
import { BTooltip } from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import VueSlider from 'vue-slider-component'
import {
  formatNumberMaxDecimalPlaces,
  formatNumberTextMaxDecimalPlaces,
} from '@/utils/formatters'
import 'vue-slider-component/theme/default.css'

export default {
  name: 'ProductFilterSlider',
  components: {
    BTooltip,
    VueSlider,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    value: {
      type: Array,
      required: true,
    },
    dataPoints: {
      type: Array,
      required: true,
    },
    customLabels: {
      type: Array,
      default: () => [],
      // Array of objects: [{ position: 0 or 1, label: 'Label', displayLabel: 'Display Label' }]
    },
    suffix: {
      type: String,
      default: '',
    },
    direction: {
      type: String,
      default: 'rtl',
      validator: value => ['rtl', 'ltr'].includes(value),
    },
    constraintKey: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isInitialized: false,
      isResetting: false,
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isLoggedIn',
      'isPremium',
    ]),
    canUseSlider() {
      return this.isPremium
        || (this.isLoggedIn && this.constraintKey === 'maturity')
    },
    premiumFeatureTooltip() {
      if (!this.isLoggedIn) {
        return 'Upgrade to Premium to use this feature'
      }
      return 'Upgrade to Premium to use this feature'
    },
    sliderValue() {
      // If value prop is valid and not empty, use it
      if (this.value && this.value.length === 2) {
        return this.value
      }
      // Otherwise use default value (full range)
      // Make sure we have valid min/max before returning default
      if (this.allDataPoints && this.allDataPoints.length > 0) {
        return this.defaultValue
      }
      // Fallback to empty array if data not loaded yet
      return []
    },
    constraintMinName() {
      return `${this.constraintKey}Min`
    },
    constraintMaxName() {
      return `${this.constraintKey}Max`
    },
    dataPointInterval() {
      // Calculate the typical interval between numeric data points only
      if (this.dataPoints.length < 2) return 1

      // Convert to numbers and filter out NaN
      const numericPoints = this.dataPoints
        .map(p => parseFloat(p))
        .filter(n => !Number.isNaN(n))
        .sort((a, b) => a - b)

      if (numericPoints.length < 2) return 1

      const diffs = []
      for (let i = 1; i < numericPoints.length; i += 1) {
        diffs.push(numericPoints[i] - numericPoints[i - 1])
      }
      // Use the minimum non-zero difference as the interval
      const validDiffs = diffs.filter(d => d > 0)
      return validDiffs.length > 0 ? Math.min(...validDiffs) : 1
    },
    allDataPoints() {
      // Convert string data points to numbers, keep only valid numbers
      const numericPoints = this.dataPoints
        .map(p => parseFloat(p))
        .filter(n => !Number.isNaN(n))

      if (numericPoints.length === 0) {
        // If no numeric points but we have custom labels, create positions for them
        if (this.customLabels.length > 0) {
          const points = []
          this.customLabels.forEach((_, index) => {
            // Position custom labels at integer positions
            points.push(index)
          })
          return points.sort((a, b) => a - b)
        }
        return []
      }

      const interval = this.dataPointInterval
      const dataMin = Math.min(...numericPoints)
      const dataMax = Math.max(...numericPoints)
      const points = [...numericPoints]

      // Add custom label positions if any
      this.customLabels.forEach(custom => {
        if (custom.position === 0) {
          // Left position - add to the left (lower value)
          const leftValue = dataMin - interval
          points.push(leftValue)
        } else if (custom.position === 1) {
          // Right position - add to the right (higher value)
          const rightValue = dataMax + interval
          points.push(rightValue)
        }
      })

      return points.sort((a, b) => a - b)
    },
    minValue() {
      return Math.min(...this.allDataPoints)
    },
    maxValue() {
      return Math.max(...this.allDataPoints)
    },
    displayedPoints() {
      const totalPoints = this.allDataPoints.length

      // If 4 or fewer points, display all
      if (totalPoints <= 4) {
        return this.allDataPoints
      }

      // More than 4 points: choose 5 if odd, 4 if even
      const labelsToShow = totalPoints % 2 === 1 ? 5 : 4

      // Select equally spaced indices
      const displayed = []
      for (let i = 0; i < labelsToShow; i += 1) {
        const index = Math.round((i * (totalPoints - 1)) / (labelsToShow - 1))
        displayed.push(this.allDataPoints[index])
      }

      return displayed
    },
    computedMarks() {
      // Return a function that vue-slider calls for each data point
      // Must return either a mark object {label: '...'} or false to hide the mark
      return val => {
        // Always show min and max marks (leftmost and rightmost)
        // Handle type coercion since dataPoints might be strings or numbers
        const numVal = typeof val === 'string' ? parseFloat(val) : val
        const isMin = numVal === this.minValue
        const isMax = numVal === this.maxValue
        const isMinOrMax = isMin || isMax

        // Check if this value should have a displayed label
        if (this.displayedPoints.includes(val) || isMinOrMax) {
          const customLabel = this.getCustomLabelForPoint(val)
          if (customLabel) {
            return {
              label: formatNumberTextMaxDecimalPlaces(customLabel.label).replace(/ /g, '\n'),
            }
          }
          return {
            label: formatNumberMaxDecimalPlaces(val) + this.suffix,
          }
        }
        // Return false to hide mark for non-displayed points
        return false
      }
    },
    sliderInterval() {
      return this.dataPointInterval
    },
    defaultValue() {
      return [this.minValue, this.maxValue]
    },
  },
  mounted() {
    // Initialize parent's sliderValues with default value
    this.$nextTick(() => {
      if (!this.value || this.value.length === 0) {
        this.$emit('input', this.defaultValue)
      }
      this.isInitialized = true
    })
    // Listen for clear-filter event from parent
    this.$parent.$on('clear-filter', this.reset)
  },
  beforeDestroy() {
    // Clean up event listener
    this.$parent.$off('clear-filter', this.reset)
  },
  methods: {
    reset() {
      // Set flag to prevent handleChange from firing during reset
      this.isResetting = true

      // Don't emit slider-filter-change here - let the parent handle batch clearing
      // Just update the slider value to default (full range)

      // Update the slider value on next tick to avoid cascading reactivity
      this.$nextTick(() => {
        this.$emit('input', this.defaultValue)
        this.$nextTick(() => {
          this.isResetting = false
        })
      })
    },
    handleDragStart() {
      // User started dragging
    },
    handleDragging(val) {
      // Update v-model during drag, but don't emit constraint change
      this.$emit('input', val)
    },
    handleDragEnd() {
      // User released the slider after dragging - emit everything using current value
      this.$emit('change', this.value)
      this.emitConstraintChange(this.value)
    },
    handleChange(val) {
      // Ignore change events during reset to prevent duplicate emissions
      if (this.isResetting) {
        return
      }
      // User released the slider - emit everything
      this.$emit('input', val)
      this.$emit('change', val)
      this.emitConstraintChange(val)
    },
    emitConstraintChange(val) {
      if (val && val.length === 2) {
        // Only emit filter change with all values in the range
        // Don't emit constraint change to avoid double filtering
        this.emitFilterChange(val)
      }
    },
    emitFilterChange(val) {
      // Don't emit filter changes until the component is fully initialized
      if (!this.isInitialized) {
        return
      }

      if (val && val.length === 2) {
        // Check if slider is at full range (min to max)
        const isFullRange = val[0] === this.minValue && val[1] === this.maxValue

        // If at full range, emit empty array (no filter constraint)
        if (isFullRange) {
          this.$parent.$emit('slider-filter-change', {
            categoryKey: this.constraintKey,
            values: [],
          })
          return
        }

        // Get all data points within the selected range
        const selectedValues = this.allDataPoints
          .filter(point => point >= val[0] && point <= val[1])
          .map(point => {
            // Check if this point has a custom label with a filter property
            const customLabel = this.getCustomLabelForPoint(point)
            if (customLabel && customLabel.filter !== undefined) {
              // Use the filter string from the custom label
              return customLabel.filter
            }
            // Otherwise use the data point value as a string
            return String(point)
          })

        // Emit filter-change event that parent will handle
        this.$parent.$emit('slider-filter-change', {
          categoryKey: this.constraintKey,
          values: selectedValues,
        })
      }
    },
    getCustomLabelForPoint(point) {
      const leftCustom = this.customLabels.find(c => c.position === 0)
      const rightCustom = this.customLabels.find(c => c.position === 1)

      const interval = this.dataPointInterval
      const min = Math.min(...this.dataPoints)
      const max = Math.max(...this.dataPoints)
      const leftValue = min - interval
      const rightValue = max + interval

      if (leftCustom && point === leftValue) {
        return leftCustom
      }
      if (rightCustom && point === rightValue) {
        return rightCustom
      }
      return null
    },
    formatValue(value) {
      const customLabel = this.getCustomLabelForPoint(value)
      if (customLabel) {
        return formatNumberTextMaxDecimalPlaces(customLabel.label).replace(/\n/g, ' ')
      }
      return formatNumberMaxDecimalPlaces(value) + this.suffix
    },
    formatDisplayValue(value) {
      const customLabel = this.getCustomLabelForPoint(value)
      if (customLabel) {
        return formatNumberTextMaxDecimalPlaces(customLabel.displayLabel || customLabel.label)
      }
      return formatNumberMaxDecimalPlaces(value) + this.suffix
    },
  },
}
</script>

<style lang="scss" scoped>
.slider-range-display {
  text-align: center;
  padding: 0.25rem 0;
  margin-top: 0.5rem;
}

::v-deep .vue-slider {
  padding: 0.5rem 0 !important;
}

::v-deep .vue-slider-rail {
  background-color: #e9ecef;
}

::v-deep .vue-slider-process {
  background-color: #1a73e8 !important;
}

::v-deep .vue-slider-dot-handle {
  background-color: #ffffff !important;
  border: 2px solid #1a73e8 !important;
  box-shadow: 0 2px 4px rgba(26, 115, 232, 0.4) !important;
}

::v-deep .vue-slider-dot-tooltip-inner {
  background-color: #1a73e8 !important;
  border: 1px solid #1a73e8 !important;
  color: #ffffff !important;
  font-weight: 500;
  border-radius: 4px;
  padding: 2px 8px;
}

::v-deep .vue-slider-dot-tooltip-inner::after {
  display: none;
}

::v-deep .vue-slider-mark-label {
  font-size: 12px;
}

::v-deep .vue-slider-mark-step {
  width: 4px !important;
  height: 4px !important;
  background-color: #1a73e8 !important;
  opacity: 1 !important;
}

::v-deep .vue-slider-mark-step-active {
  opacity: 1 !important;
}

.dark-layout ::v-deep .vue-slider-rail {
  background-color: #283046;
}

.slider-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.slider-disabled ::v-deep .vue-slider {
  cursor: not-allowed;
}
</style>
